import { Button, Divider, Grid } from "@mui/material";
import { Add as AddIcon, Person as PersonIcon } from "@mui/icons-material";
import React from "react";

import useDeviceInformation from "components/pages/transmissions/modal/patient/tabs/deviceInformation/useDeviceInformation";

import AddNewDeviceModal from "components/common/modals/addNewDeviceModal/AddNewDeviceModal";
import LeadInformation from "components/pages/transmissions/modal/patient/tabs/deviceInformation/leadInformation/LeadInformation";

import ConfirmModal from "components/common/modals/confirmModal/ConfirmModal";

import { USER_ROLES } from "static/constants";
import formatDate from "utils/formatDate";
import SecondaryDeviceInformation from "./secondaryDeviceInformation/SecondaryDeviceInformation";
import PrimaryDeviceInformation from "./primaryDeviceInfomration/PrimaryDeviceInformation";
import PreviousPrimaryDeviceInformationPreview from "./previousPrimaryDeviceInformation/PreviousPrimaryDeviceInformationPreview";
import DuplicatePatientDevicesModal from "./modal/DuplicatePatientDevicesModal";
import { showErrorToast } from "utils/toast";

const DeviceInformation = () => {
  const {
    profile,
    isLoading,
    isEditMode,
    isSubmitting,
    primaryDevice,
    previousDevices,
    currentUserRole,
    secondaryDevice,
    showSecondaryDevice,
    isCreatePatientModal,
    showAddNewDeviceModal,
    isSecondaryDeviceEditMode,
    showAddNewDeviceSuccessPrompt,
    isSecondaryDeviceSubmitLoading,
    deviceInformationEditAccessRoles,

    handleSubmit,
    setIsEditMode,
    handleCancelClick,
    handleAddNewDevice,
    handleCreatePatient,
    setShowSecondaryDevice,
    setIsCreatePatientModal,
    setShowAddNewDeviceModal,
    setIsSecondaryDeviceEditMode,
    handleAddNewDeviceModalClick,
    setShowAddNewDeviceSuccessPrompt,
    devices,

    selectedDevice,
    setSelectedDevice,

    isDuplicatePatientConfirmModal,
    setIsDuplicatePatientConfirmModal,
  } = useDeviceInformation();

  return (
    <>
      <div className="patient-modal__section">
        <div className="patient-form__row patient-form__info">
          <div className="mb-3">
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item style={{ textAlign: "left" }}>
                <div className="patient-form__info--header">
                  Device Information
                </div>
              </Grid>
              <Grid item>
                <Grid
                  container
                  item
                  alignItems="center"
                  spacing={4}
                  style={{ gap: "10px" }}
                >
                  {[
                    USER_ROLES.ADMIN,
                    USER_ROLES.USER,
                    USER_ROLES.CLINIC_ADMIN,
                  ].includes(currentUserRole) && (
                    <>
                      {!isEditMode && devices?.length > 1 && (
                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={() => setIsCreatePatientModal(true)}
                        >
                          <PersonIcon />
                          Create Duplicate Patient
                        </Button>
                      )}

                      {!isEditMode && (
                        <>
                          <Button
                            variant="contained"
                            color="secondary"
                            className="rpm-device-information__add-rpm-device mr-2"
                            onClick={handleAddNewDeviceModalClick}
                          >
                            <AddIcon className="rpm-device-information__add-icon" />
                            <span className="rpm-device-information__add-text">
                              Add New Device
                            </span>
                          </Button>
                          {showAddNewDeviceModal && (
                            <AddNewDeviceModal
                              currentDevice={primaryDevice?.modelName}
                              patientId={profile?.patientId}
                              isModalOpen={showAddNewDeviceModal}
                              handleAddNewDevice={handleAddNewDevice}
                              onClose={() => setShowAddNewDeviceModal(false)}
                            />
                          )}
                        </>
                      )}
                    </>
                  )}

                  {!showSecondaryDevice && (
                    <Button
                      variant="contained"
                      color="secondary"
                      className="rpm-device-information__add-rpm-device mr-2"
                      onClick={() => {
                        setShowSecondaryDevice(true);
                        setIsSecondaryDeviceEditMode(true);
                      }}
                    >
                      <AddIcon className="rpm-device-information__add-icon" />
                      <span className="rpm-device-information__add-text">
                        Add Secondary Device
                      </span>
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Grid>

            <PrimaryDeviceInformation
              handleSubmit={handleSubmit}
              currentUserRole={currentUserRole}
              deviceInformation={primaryDevice}
              handleCancelClick={handleCancelClick}
              isEditMode={isEditMode}
              isLoading={isLoading}
              setIsEditMode={setIsEditMode}
              deviceInformationEditAccessRoles={
                deviceInformationEditAccessRoles
              }
              previousDevices={previousDevices}
            />
            {showSecondaryDevice && (
              <>
                <Divider style={{ marginTop: "20px" }} />
                <Grid container style={{ marginTop: "20px" }}>
                  <SecondaryDeviceInformation
                    handleSubmit={handleSubmit}
                    currentUserRole={currentUserRole}
                    deviceInformation={secondaryDevice}
                    handleCancelClick={handleCancelClick}
                    isEditMode={isSecondaryDeviceEditMode}
                    isLoading={isSecondaryDeviceSubmitLoading}
                    setIsEditMode={setIsSecondaryDeviceEditMode}
                    deviceInformationEditAccessRoles={
                      deviceInformationEditAccessRoles
                    }
                  />
                </Grid>
              </>
            )}

            {previousDevices && previousDevices.length > 0 && (
              <>
                <Divider style={{ marginTop: "20px" }} />
                <PreviousPrimaryDeviceInformationPreview
                  previousDevices={previousDevices}
                />
              </>
            )}
          </div>
        </div>
      </div>
      <LeadInformation />

      {devices?.length > 0 && isCreatePatientModal && (
        <DuplicatePatientDevicesModal
          devices={devices}
          show={isCreatePatientModal}
          title="Create New Patient From Device"
          onClose={() => {
            setIsCreatePatientModal(false);
            setSelectedDevice({});
          }}
          selectedDevice={selectedDevice}
          isSubmitting={isSubmitting}
          onSelected={(selected) => {
            if (selected.device_model && selected.device_serial) {
              setIsDuplicatePatientConfirmModal(true);
              setSelectedDevice(selected);
            } else {
              showErrorToast(
                "The device you selected has missing / Invalid device model or device serial. Update the information and try again."
              );
            }
          }}
        />
      )}

      {isDuplicatePatientConfirmModal && (
        <ConfirmModal
          confirmText="Yes"
          title="Create New Patient For Active Device"
          show={isDuplicatePatientConfirmModal}
          isConfirming={isSubmitting}
          onConfirm={handleCreatePatient}
          onCancel={() => {
            setIsDuplicatePatientConfirmModal(false);
            setSelectedDevice({});
          }}
          children={
            <span>
              This action will create a new patient with{" "}
              <b>{selectedDevice?.modelName}</b>,{" "}
              <b>{formatDate(selectedDevice?.doi)}</b> and recent previous
              device details are considered active. Are you sure you want to do
              this?
            </span>
          }
        />
      )}

      {showAddNewDeviceSuccessPrompt && (
        <ConfirmModal
          confirmText="Confirm"
          title="Change Device"
          show={showAddNewDeviceSuccessPrompt}
          onConfirm={() => {
            setShowSecondaryDevice(true);
            setIsSecondaryDeviceEditMode(true);
            setShowAddNewDeviceSuccessPrompt(false);
          }}
          onCancel={() => {
            setShowAddNewDeviceSuccessPrompt(false);
          }}
          children={
            <span>
              Primary device has been updated successfully. Do you want to edit
              the secondary device too?
            </span>
          }
        />
      )}
    </>
  );
};

export default DeviceInformation;
